import { useEffect } from "react";

import { Controller } from "react-hook-form";

import { Select, TextInput } from "@components/v2";
import { Label } from "@components/v2/typography/label";
import { useScheduleExportFormContext } from "@features/data-export-v2/hooks/useScheduleExportFormContext";
import { ExportFormats } from "@north-beam/nb-common";

import { ScheduledExportHeader } from "../components/scheduled-export-form/scheduled-export-header";

import { GCSBucketValidation } from "./gcs/gcs-bucket-validation";
import { GCSConfigurationInstructions } from "./gcs/gcs-configuration-instructions";
import { S3BucketValidation } from "./s3/s3-bucket-validation";
import { S3ConfigurationInstructions } from "./s3/s3-configuration-instructions";

export const DestinationForm = () => {
  const scheduleExportForm = useScheduleExportFormContext();

  const destinationType = scheduleExportForm.watch("destination.type");
  const isGCSDestination = destinationType === "GCS";
  const isS3Destination = destinationType === "S3";

  useEffect(() => {
    scheduleExportForm.setValue("destination.isBucketValid", false);
    scheduleExportForm.setValue("destination.bucketName", "");
  }, [destinationType]);

  return (
    <>
      <ScheduledExportHeader
        title="Destination"
        description="Select where the report will be stored."
      />
      <div className="grid grid-cols-2 gap-6 mt-4 w-full">
        <div className="flex flex-col gap-4 w-full">
          <Label label="Platform" variant={300} className="max-w-sm">
            <Controller
              name="destination.type"
              control={scheduleExportForm.control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  size="md"
                  value={field.value}
                  options={[
                    { value: "GCS", label: "Google Cloud Storage" },
                    { value: "S3", label: "Amazon S3" },
                  ]}
                  onChange={field.onChange}
                  buttonClassName="w-full"
                />
              )}
            />
          </Label>
          {isGCSDestination && (
            <>
              <Label label="Export format" variant={300} className="max-w-sm">
                <Controller
                  name="destination.exportFormat"
                  control={scheduleExportForm.control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      options={ExportFormats.map((format) => ({
                        value: format,
                        label: format,
                      }))}
                      onChange={field.onChange}
                      buttonClassName="w-full"
                      disabled
                    />
                  )}
                />
              </Label>
              <Label label="File name" variant={300} className="max-w-sm">
                <Controller
                  name="destination.fileName"
                  control={scheduleExportForm.control}
                  rules={{ required: true, minLength: 3 }}
                  render={({ field }) => (
                    <TextInput
                      size="md"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      placeholder="Enter file name"
                    />
                  )}
                />
              </Label>
              <Label label="GCS bucket" variant={300} className="max-w-sm">
                <Controller
                  name="destination.bucketName"
                  control={scheduleExportForm.control}
                  rules={{ required: true, minLength: 3 }}
                  render={({ field }) => (
                    <TextInput
                      size="md"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        scheduleExportForm.setValue(
                          "destination.isBucketValid",
                          false,
                        );
                      }}
                      placeholder="Enter bucket name"
                    />
                  )}
                />
              </Label>
              <GCSBucketValidation
                bucketName={scheduleExportForm.watch("destination.bucketName")}
                isBucketValid={scheduleExportForm.watch(
                  "destination.isBucketValid",
                )}
                onValidated={() =>
                  scheduleExportForm.setValue("destination.isBucketValid", true)
                }
              />
            </>
          )}
          {isS3Destination && (
            <>
              <Label label="Export format" variant={300} className="max-w-sm">
                <Controller
                  name="destination.exportFormat"
                  control={scheduleExportForm.control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      options={ExportFormats.map((format) => ({
                        value: format,
                        label: format,
                      }))}
                      onChange={field.onChange}
                      buttonClassName="w-full"
                      disabled
                    />
                  )}
                />
              </Label>
              <Label label="File name" variant={300} className="max-w-sm">
                <Controller
                  name="destination.fileName"
                  control={scheduleExportForm.control}
                  rules={{ required: true, minLength: 3 }}
                  render={({ field }) => (
                    <TextInput
                      size="md"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      placeholder="Enter file name"
                    />
                  )}
                />
              </Label>
              <Label label="S3 bucket" variant={300} className="max-w-sm">
                <Controller
                  name="destination.bucketName"
                  control={scheduleExportForm.control}
                  rules={{ required: true, minLength: 3 }}
                  render={({ field }) => (
                    <TextInput
                      size="md"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        scheduleExportForm.setValue(
                          "destination.isBucketValid",
                          false,
                        );
                      }}
                      placeholder="Enter bucket name"
                    />
                  )}
                />
              </Label>
              <Label label="AWS Role ARN" variant={300} className="max-w-sm">
                <Controller
                  name="destination.awsRole"
                  control={scheduleExportForm.control}
                  rules={{ required: true, minLength: 3 }}
                  render={({ field }) => (
                    <TextInput
                      size="md"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        scheduleExportForm.setValue(
                          "destination.isBucketValid",
                          false,
                        );
                      }}
                      placeholder="arn:aws:iam::xxxxxxxxxx:role/name"
                    />
                  )}
                />
              </Label>
              <S3BucketValidation
                bucketName={scheduleExportForm.watch("destination.bucketName")}
                awsRole={scheduleExportForm.watch("destination.awsRole")}
                isBucketValid={scheduleExportForm.watch(
                  "destination.isBucketValid",
                )}
                onValidated={() =>
                  scheduleExportForm.setValue("destination.isBucketValid", true)
                }
              />
            </>
          )}
        </div>
        {isGCSDestination && <GCSConfigurationInstructions />}
        {isS3Destination && <S3ConfigurationInstructions />}
      </div>
    </>
  );
};
